import queryString from "query-string";
import { useLocation } from "react-router-dom";
import type { LocationState } from "src/App";
import isString from "lodash/isString";

// We only allow redirecting back to the localhost server of the extension.
// Example of valid redirect_uri:
// 'http%3A%2F%2Flocalhost%3A58853%2Flogin'
// decodeURIComponent -> 'http://localhost:58853/login'

const validRedirectUriRegex =
  /^http:\/\/localhost:(?<port>\d+)\/login$|(https:\/\/store\.lc-staging1\.staging-k8slens\.cloud\/api\/auth\/callback\/lensid|https:\/\/store\.k8slens\.dev\/api\/auth\/callback\/lensid)|(https:\/\/api\.lenscloud\.io)\/auth\/lenscloud\/callback|(https:\/\/checkout\.lc-staging1\.staging-k8slens\.cloud\/auth\/callback|http:\/\/localhost:3000\/auth\/callback)$/;
const validRedirectUris = ["/support", "/forums"];

const minPortNumber = 1;
const maxPortNumber = 65535;

const isValidRedirectUri = (redirectUri: string | undefined) => {
  if (!isString(redirectUri)) {
    return false;
  }

  if (validRedirectUris.includes(redirectUri)) {
    return true;
  }

  // In addition to http://localhost:[port]/login in `validRedirectUriRegex`, we allow redirecting to http://localhost* in development/test environments
  if (process.env.NODE_ENV !== "production" && redirectUri.startsWith("http://localhost")) {
    return true;
  }

  const redirectUrl = new URL(redirectUri);

  if (
    redirectUrl.origin === window.location.origin &&
    redirectUrl.pathname === "/signin/realms/lensCloud/protocol/openid-connect/auth"
  ) {
    return true;
  }

  const matches = redirectUri.match(validRedirectUriRegex);

  if (!matches) {
    return false;
  }

  const { port } = matches.groups as any as { port?: number };

  if (port) {
    return minPortNumber <= port && port <= maxPortNumber;
  }

  return true;
};

const includeRedirectParameters = (queryParams: queryString.ParsedQuery<string>) => Boolean(queryParams.include_params);

/**
 * Return a whitelisted redirect_uri from window.location.search (?redirect_uri=), or undefined otherwise
 */
export const useRedirectUri = () => {
  const location = useLocation<LocationState | undefined>();
  const queryParams = queryString.parse(location.search);
  const redirectUri = queryParams.redirect_uri?.toString();
  const state = queryParams.state?.toString();
  // These two are from "Lens Forum"
  const sso = queryParams.sso?.toString();
  const sig = queryParams.sig?.toString();

  if (!isValidRedirectUri(redirectUri)) {
    return undefined;
  }

  // In some cases we want to pass additional query parameters in redirect_uri
  // This is only done if include_params query parameter is set
  // Otherwise we return redirect_uri as is
  if (!includeRedirectParameters(queryParams)) {
    // keep the original state in query params
    if (state) {
      return `${redirectUri}?state=${encodeURIComponent(state)}`;
    }

    if (sso && sig) {
      return `${redirectUri}?sig=${encodeURIComponent(sig)}&sso=${encodeURIComponent(sso)}`;
    }

    return redirectUri;
  }

  // Remove redirect_uri and include_params from query params
  // because we don't want to pass them to the redirect_uri,
  // as they're unnecessary in it.
  delete queryParams.redirect_uri;
  delete queryParams.include_params;

  let redirectQueryParams = "";

  // keep rest of the queryParams in redirect uri if any
  if (Object.keys(queryParams).length > 0) {
    redirectQueryParams = `?${queryString.stringify(queryParams)}`;
  }

  const redirectUriWithQueryParams = `${redirectUri}${redirectQueryParams}`;

  return redirectUriWithQueryParams;
};
